import type { Editor, JSONContent } from '@tiptap/core';

import { createSingleEditorTaskListContentWithItems } from '../../../../common/taskLists/singleEditorTaskListContentBuilder';
import { duplicateTaskItem } from '../../../../common/tiptap/extensions/taskList/taskListItemDuplicationUtils';
import { deleteTaskItemWithTransaction } from '../../../../common/tiptap/extensions/taskList/taskListItemDropUtils';
import { getNewTransactionId } from '../../../utils/undoRedo/undoRedoTransactionManager';
import { createElementSync } from '../../actions/elementActions';
import { ElementType } from '../../../../common/elements/elementTypes';
import type { MNElementLocation } from '../../../../common/elements/elementModelTypes';

type TaskItemDragOutArguments = {
    taskItem: JSONContent;
    // A canvas point or a column insertion point, already translated from the drop result
    location: MNElementLocation;
    dispatch: (action: unknown) => void;
};

/**
 * Moves a dragged task item out of its list and into a new single-editor task list element.
 *
 * The source deletion and the creation share one app transaction, so a single undo reverses the
 * whole drag (item restored in the source list, new element removed).
 */
export const moveTaskItemToNewTaskListElement = ({
    sourceEditor,
    sourcePosition,
    taskItem,
    location,
    dispatch,
}: TaskItemDragOutArguments & { sourceEditor: Editor; sourcePosition: number }): void => {
    const transactionId = getNewTransactionId();

    deleteTaskItemWithTransaction({ editor: sourceEditor, position: sourcePosition, transactionId });

    dispatch(
        createElementSync({
            elementType: ElementType.TASK_LIST_TYPE,
            location,
            content: createSingleEditorTaskListContentWithItems([taskItem]),
            transactionId,
        }),
    );
};

/**
 * Copies a dragged task item into a new single-editor task list element, leaving the source list
 * untouched. Only the creation happens, so a single undo removes the new list.
 *
 * The copy carries fresh task IDs before the create is dispatched, because reminder reconciliation
 * keys rows by `(elementId, taskId)` on that first create.
 *
 * Flagged as a duplicate so the new element does not adopt the comment threads behind any inline
 * comment marks it copied - those threads belong to the source, which still exists.
 */
export const copyTaskItemToNewTaskListElement = ({ taskItem, location, dispatch }: TaskItemDragOutArguments): void => {
    const duplicatedTaskItemContent = duplicateTaskItem(taskItem);

    dispatch(
        createElementSync({
            elementType: ElementType.TASK_LIST_TYPE,
            location,
            content: createSingleEditorTaskListContentWithItems([duplicatedTaskItemContent]),
            transactionId: getNewTransactionId(),
            duplicate: true,
        }),
    );
};
